import React, { useEffect, useMemo, useState } from "react";
import {
  Download, CalendarPlus, MapPin, Clock, Loader2, Plus, Pencil, Trash2, X, ExternalLink, Building2,
  List, LayoutGrid, ChevronLeft, ChevronRight, Check, HelpCircle, Users,
} from "lucide-react";
import api, { downloadPdf } from "../../lib/api";
import { PageTitle, Card, Badge, Loading, fmtDate } from "./ui";
import { useAuth } from "../../context/AuthContext";

const CATS = {
  Zetland: { label: "Zetland (Internal)", dot: "bg-sapphire-deep", chip: "border-sapphire-deep text-sapphire-deep", bar: "bg-sapphire-deep", text: "text-sapphire-deep" },
  UGLE: { label: "UGLE", dot: "bg-garnet", chip: "border-garnet text-garnet", bar: "bg-garnet", text: "text-garnet" },
  DGLE: { label: "DGLE", dot: "bg-gold-deep", chip: "border-gold-deep text-gold-deep", bar: "bg-gold-deep", text: "text-gold-deep" },
  "Singapore Lodges": { label: "Singapore Lodges", dot: "bg-emerald", chip: "border-emerald text-emerald", bar: "bg-emerald", text: "text-emerald" },
};
const CAT_KEYS = Object.keys(CATS);
const catOf = (e) => e.category ?? "Zetland";

function googleCalUrl(e) {
  const start = new Date(e.date);
  const end = new Date(start.getTime() + 3 * 60 * 60 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const details = [e.type, e.description, e.source_org, e.time && `Time: ${e.time}`]
    .filter(Boolean).join("\n");
  const params = new URLSearchParams({
    action: "TEMPLATE", text: e.title, dates: `${fmt(start)}/${fmt(end)}`,
    details, location: e.location || "",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function downloadIcs(e) {
  const start = new Date(e.date);
  const end = new Date(start.getTime() + 3 * 60 * 60 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const ics = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Zetland508//EN", "BEGIN:VEVENT",
    `UID:${e.id}@zetland508.org`, `DTSTAMP:${fmt(new Date())}`, `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`, `SUMMARY:${e.title}`, `LOCATION:${e.location || ""}`,
    `DESCRIPTION:${e.type || ""}`, "END:VEVENT", "END:VCALENDAR"].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url; a.download = `${e.title.replace(/\s+/g, "-").toLowerCase()}.ics`; a.click();
  URL.revokeObjectURL(url);
}

const EMPTY = { title: "", type: "Regular Meeting", category: "Zetland", date: "", time: "", location: "", description: "", source_org: "", summons: false };

function EventModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY, ...initial,
    date: initial?.date ? new Date(initial.date).toISOString().slice(0, 16) : "",
  }));
  const [saving, setSaving] = useState(false);
  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, date: new Date(form.date).toISOString() };
      if (initial?.id) await api.put(`/officer/events/${initial.id}`, payload);
      else await api.post("/officer/events", payload);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-sapphire-deep/40 p-4 backdrop-blur-sm" data-testid="event-modal">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-7 shadow-heritage">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl font-semibold text-sapphire-deep">{initial?.id ? "Edit Event" : "Add Event"}</h3>
          <button onClick={onClose} className="text-ink/50 hover:text-garnet"><X size={22} /></button>
        </div>
        <div className="mt-5 space-y-4">
          <input className="input-lodge" placeholder="Title" value={form.title} onChange={up("title")} data-testid="event-title" />
          <div className="grid grid-cols-2 gap-4">
            <select className="input-lodge" value={form.category} onChange={up("category")} data-testid="event-category">
              {CAT_KEYS.map((c) => <option key={c} value={c}>{CATS[c].label}</option>)}
            </select>
            <input className="input-lodge" placeholder="Type (e.g. Installation)" value={form.type} onChange={up("type")} />
          </div>
          <input type="datetime-local" className="input-lodge" value={form.date} onChange={up("date")} data-testid="event-date" />
          <div className="grid grid-cols-2 gap-4">
            <input className="input-lodge" placeholder="Time label (e.g. Tyling 6:30 PM)" value={form.time} onChange={up("time")} />
            <input className="input-lodge" placeholder="Organising body" value={form.source_org} onChange={up("source_org")} />
          </div>
          <input className="input-lodge" placeholder="Location" value={form.location} onChange={up("location")} />
          <textarea className="input-lodge resize-none" rows={3} placeholder="Description" value={form.description} onChange={up("description")} />
          <label className="flex items-center gap-2 font-sans text-sm text-ink/70">
            <input type="checkbox" checked={form.summons} onChange={up("summons")} className="h-4 w-4 accent-sapphire-deep" /> Has a summons (internal meetings)
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="btn-dark">Cancel</button>
          <button onClick={save} disabled={saving || !form.title || !form.date} className="btn-sapphire" data-testid="event-save">
            {saving ? <Loader2 size={15} className="animate-spin" /> : null} Save
          </button>
        </div>
      </div>
    </div>
  );
}

const RSVP_META = {
  yes: { label: "Attending", icon: Check, on: "bg-emerald text-white border-emerald", off: "border-emerald/40 text-emerald hover:bg-emerald/10" },
  maybe: { label: "Maybe", icon: HelpCircle, on: "bg-gold text-sapphire-deep border-gold", off: "border-gold/50 text-gold-deep hover:bg-gold/10" },
  no: { label: "Can't attend", icon: X, on: "bg-garnet text-white border-garnet", off: "border-garnet/40 text-garnet hover:bg-garnet/10" },
};

function RsvpModal({ event, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/officer/events/${event.id}/rsvps`).then((r) => setData(r.data)).catch(() => setData({ counts: { yes: 0, no: 0, maybe: 0 }, responses: [] }));
  }, [event.id]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-sapphire-deep/40 p-4 backdrop-blur-sm" data-testid="rsvp-modal">
      <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-7 shadow-heritage">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-sans text-xs font-semibold uppercase tracking-widest text-gold-deep">Expected Attendance</p>
            <h3 className="font-display text-xl font-semibold text-sapphire-deep">{event.title}</h3>
          </div>
          <button onClick={onClose} className="text-ink/50 hover:text-garnet"><X size={22} /></button>
        </div>
        {!data ? <div className="py-8 text-center"><Loader2 className="mx-auto animate-spin text-sapphire-deep" /></div> : (
          <>
            <div className="mt-5 grid grid-cols-3 gap-3 text-center">
              {["yes", "maybe", "no"].map((k) => (
                <div key={k} className="rounded-xl bg-sapphire-deep/[0.05] py-3">
                  <p className="font-display text-2xl font-semibold text-sapphire-deep" data-testid={`rsvp-count-${k}`}>{data.counts[k]}</p>
                  <p className="font-sans text-[10px] uppercase tracking-widest text-ink/50">{RSVP_META[k].label}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 divide-y divide-sapphire-deep/8">
              {data.responses.length === 0 ? (
                <p className="py-4 font-sans text-sm italic text-ink/40">No responses yet.</p>
              ) : data.responses.map((r) => {
                const m = RSVP_META[r.status]; const Icon = m.icon;
                return (
                  <div key={r.id} className="flex items-center justify-between py-2.5">
                    <div>
                      <p className="font-sans text-sm font-medium text-sapphire-deep">{r.name}</p>
                      <p className="font-sans text-xs text-ink/50">{r.rank}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-sans text-xs font-semibold ${m.on}`}>
                      <Icon size={13} /> {m.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EventCard({ e, user, busy, onSummons, onEdit, onRemove, onRsvp, onViewRsvps }) {
  const cat = CATS[catOf(e)];
  const isInternal = catOf(e) === "Zetland";
  const counts = e.rsvp_counts || { yes: 0, maybe: 0, no: 0 };
  return (
    <Card className="relative overflow-hidden" data-testid={`event-${e.id}`}>
      <span className={`absolute inset-y-0 left-0 w-1.5 ${cat.bar}`} />
      <div className="flex flex-col gap-4 pl-3 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-5">
          <div className="flex h-16 w-16 shrink-0 flex-col items-center justify-center rounded-xl bg-sapphire-deep/[0.06] text-sapphire-deep">
            <span className="font-display text-2xl font-semibold leading-none">{new Date(e.date).getDate()}</span>
            <span className="font-sans text-[10px] uppercase tracking-widest">{new Date(e.date).toLocaleString("en-GB", { month: "short" })}</span>
          </div>
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-wide ${cat.chip}`}>
                <span className={`h-2 w-2 rounded-full ${cat.dot}`} /> {catOf(e)}
              </span>
              <Badge>{e.type}</Badge>
            </div>
            <h3 className="font-display text-xl font-semibold text-sapphire-deep">{e.title}</h3>
            {e.source_org && <p className="mt-0.5 flex items-center gap-1 font-sans text-xs text-ink/50"><Building2 size={12} /> {e.source_org}</p>}
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 font-sans text-sm text-ink/60">
              <span>{fmtDate(e.date)}</span>
              {e.time && <span className="flex items-center gap-1"><Clock size={14} /> {e.time}</span>}
              {e.location && <span className="flex items-center gap-1"><MapPin size={14} /> {e.location}</span>}
            </div>
            {e.description && <p className="mt-2 max-w-2xl font-sans text-sm text-ink/70">{e.description}</p>}

            {isInternal && (
              <div className="mt-4 rounded-xl border border-sapphire-deep/10 bg-ivory/60 p-3" data-testid={`rsvp-block-${e.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="font-sans text-xs font-semibold uppercase tracking-widest text-stonegrey">Will you attend?</span>
                  <div className="flex gap-2">
                    {["yes", "maybe", "no"].map((k) => {
                      const m = RSVP_META[k]; const Icon = m.icon; const on = e.my_rsvp === k;
                      return (
                        <button key={k} onClick={() => onRsvp(e, k)}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 font-sans text-xs font-semibold transition ${on ? m.on : m.off}`}
                          data-testid={`rsvp-${k}-${e.id}`}>
                          <Icon size={13} /> {m.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <p className="font-sans text-xs text-ink/50" data-testid={`rsvp-summary-${e.id}`}>
                    <span className="font-semibold text-emerald">{counts.yes}</span> attending · {counts.maybe} maybe · {counts.no} declined
                  </p>
                  {user?.is_officer && (
                    <button onClick={() => onViewRsvps(e)} className="inline-flex items-center gap-1 font-sans text-xs font-semibold text-sapphire-deep hover:text-gold-deep" data-testid={`view-rsvps-${e.id}`}>
                      <Users size={13} /> View responses
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2 md:flex-col md:items-end">
          <a href={googleCalUrl(e)} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 rounded-full bg-sapphire-deep px-4 py-2 font-sans text-xs font-semibold uppercase tracking-widest text-parchment transition hover:bg-sapphire-mid"
            data-testid={`gcal-${e.id}`}>
            <CalendarPlus size={14} /> Google Calendar <ExternalLink size={12} />
          </a>
          <div className="flex gap-2">
            <button onClick={() => downloadIcs(e)} className="rounded-full border border-sapphire-deep/15 px-3 py-2 font-sans text-xs font-semibold uppercase tracking-widest text-ink/60 transition hover:border-gold hover:text-gold-deep">.ics</button>
            {e.summons && (
              <button onClick={() => onSummons(e)} disabled={busy === e.id}
                className="flex items-center gap-1 rounded-full border border-gold/40 px-3 py-2 font-sans text-xs font-semibold uppercase tracking-widest text-gold-deep transition hover:bg-gold hover:text-sapphire-deep disabled:opacity-50">
                {busy === e.id ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Summons
              </button>
            )}
          </div>
          {user?.is_officer && (
            <div className="flex gap-2">
              <button onClick={() => onEdit(e)} className="rounded-full border border-sapphire-deep/15 p-2 text-sapphire-deep hover:border-gold hover:text-gold-deep" data-testid={`edit-event-${e.id}`}><Pencil size={13} /></button>
              <button onClick={() => onRemove(e)} className="rounded-full border border-garnet/30 p-2 text-garnet hover:bg-garnet/10" data-testid={`delete-event-${e.id}`}><Trash2 size={13} /></button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function MonthGrid({ events, month, setMonth, selectedDay, setSelectedDay }) {
  const y = month.getFullYear(), m = month.getMonth();
  const firstDay = new Date(y, m, 1).getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const byDay = {};
  events.forEach((e) => {
    const d = new Date(e.date);
    if (d.getFullYear() === y && d.getMonth() === m) (byDay[d.getDate()] ||= []).push(e);
  });
  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);
  const today = new Date();
  const isToday = (d) => d && today.getFullYear() === y && today.getMonth() === m && today.getDate() === d;
  const step = (delta) => { setMonth(new Date(y, m + delta, 1)); setSelectedDay(null); };

  return (
    <Card className="mb-6" data-testid="calendar-month-grid">
      <div className="mb-4 flex items-center justify-between">
        <button onClick={() => step(-1)} className="rounded-full border border-sapphire-deep/15 p-2 text-sapphire-deep hover:border-gold hover:text-gold-deep" data-testid="month-prev"><ChevronLeft size={18} /></button>
        <h3 className="font-display text-2xl font-semibold text-sapphire-deep" data-testid="month-label">
          {month.toLocaleString("en-GB", { month: "long", year: "numeric" })}
        </h3>
        <button onClick={() => step(1)} className="rounded-full border border-sapphire-deep/15 p-2 text-sapphire-deep hover:border-gold hover:text-gold-deep" data-testid="month-next"><ChevronRight size={18} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="pb-2 text-center font-sans text-[10px] font-semibold uppercase tracking-widest text-ink/40">{w}</div>
        ))}
        {cells.map((d, i) => {
          const evs = d ? (byDay[d] || []) : [];
          const sel = d && selectedDay && selectedDay.getDate() === d && selectedDay.getMonth() === m && selectedDay.getFullYear() === y;
          return (
            <div key={i}
              onClick={() => d && setSelectedDay(sel ? null : new Date(y, m, d))}
              className={`min-h-[92px] rounded-lg border p-1.5 transition ${
                d ? "cursor-pointer border-sapphire-deep/10 hover:border-gold/50" : "border-transparent"
              } ${sel ? "border-gold bg-gold/10" : evs.length ? "bg-sapphire-deep/[0.03]" : "bg-white"}`}
              data-testid={d ? `month-day-${d}` : undefined}>
              {d && (
                <>
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full font-sans text-xs ${isToday(d) ? "bg-sapphire-deep font-semibold text-parchment" : "text-ink/70"}`}>{d}</span>
                  <div className="mt-1 space-y-1">
                    {evs.slice(0, 3).map((e) => (
                      <div key={e.id} className="flex items-center gap-1 truncate">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${CATS[catOf(e)].dot}`} />
                        <span className="truncate font-sans text-[10px] text-ink/70">{e.title}</span>
                      </div>
                    ))}
                    {evs.length > 3 && <span className="font-sans text-[9px] font-semibold text-ink/40">+{evs.length - 3} more</span>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState(null);
  const [active, setActive] = useState(Object.fromEntries(CAT_KEYS.map((c) => [c, true])));
  const [busy, setBusy] = useState(null);
  const [modal, setModal] = useState(null);
  const [rsvpModal, setRsvpModal] = useState(null);
  const [view, setView] = useState("list");
  const [month, setMonth] = useState(() => new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  const load = () => api.get("/portal/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  useEffect(() => { load(); }, []);

  const toggle = (c) => setActive((a) => ({ ...a, [c]: !a[c] }));

  const dlSummons = async (e) => {
    setBusy(e.id);
    try { await downloadPdf(`/portal/events/${e.id}/summons.pdf`, `summons-${e.date?.slice(0, 10)}.pdf`); }
    finally { setBusy(null); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    await api.delete(`/officer/events/${e.id}`);
    load();
  };

  const rsvp = async (e, status) => {
    await api.post(`/portal/events/${e.id}/rsvp`, { status });
    load();
  };

  const shown = useMemo(
    () => (events || []).filter((e) => active[catOf(e)]),
    [events, active]
  );

  const monthList = useMemo(() => {
    if (!selectedDay) return shown.filter((e) => {
      const d = new Date(e.date);
      return d.getFullYear() === month.getFullYear() && d.getMonth() === month.getMonth();
    });
    return shown.filter((e) => {
      const d = new Date(e.date);
      return d.toDateString() === selectedDay.toDateString();
    });
  }, [shown, month, selectedDay]);

  if (!events) return <Loading />;

  const cardProps = { user, busy, onSummons: dlSummons, onEdit: setModal, onRemove: remove, onRsvp: rsvp, onViewRsvps: setRsvpModal };

  return (
    <div data-testid="calendar-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageTitle eyebrow="Lodge Diary" title="Calendar"
          sub="A layered view of Zetland meetings and the wider Masonic calendar." />
        <div className="flex items-center gap-3">
          <div className="flex overflow-hidden rounded-full border border-sapphire-deep/15" data-testid="view-toggle">
            <button onClick={() => setView("list")} className={`flex items-center gap-1.5 px-4 py-2 font-sans text-xs font-semibold uppercase tracking-widest transition ${view === "list" ? "bg-sapphire-deep text-parchment" : "text-ink/50 hover:text-sapphire-deep"}`} data-testid="view-list">
              <List size={14} /> List
            </button>
            <button onClick={() => setView("month")} className={`flex items-center gap-1.5 px-4 py-2 font-sans text-xs font-semibold uppercase tracking-widest transition ${view === "month" ? "bg-sapphire-deep text-parchment" : "text-ink/50 hover:text-sapphire-deep"}`} data-testid="view-month">
              <LayoutGrid size={14} /> Month
            </button>
          </div>
          {user?.is_officer && (
            <button onClick={() => setModal({})} className="btn-sapphire" data-testid="add-event-btn">
              <Plus size={16} /> Add Event
            </button>
          )}
        </div>
      </div>

      {/* Layer filters */}
      <div className="mb-8 flex flex-wrap gap-3" data-testid="calendar-filters">
        {CAT_KEYS.map((c) => (
          <button
            key={c}
            onClick={() => toggle(c)}
            className={`flex items-center gap-2 rounded-full border px-4 py-2 font-sans text-xs font-semibold uppercase tracking-widest transition ${
              active[c] ? `${CATS[c].chip} bg-white` : "border-sapphire-deep/15 text-ink/40"
            }`}
            data-testid={`filter-${c.split(" ")[0].toLowerCase()}`}
          >
            <span className={`h-2.5 w-2.5 rounded-full ${active[c] ? CATS[c].dot : "bg-ink/20"}`} />
            {CATS[c].label}
          </button>
        ))}
      </div>

      {view === "month" ? (
        <>
          <MonthGrid events={shown} month={month} setMonth={setMonth} selectedDay={selectedDay} setSelectedDay={setSelectedDay} />
          <h3 className="mb-4 font-display text-xl font-semibold text-sapphire-deep">
            {selectedDay
              ? `Events on ${selectedDay.toLocaleDateString("en-GB", { day: "numeric", month: "long" })}`
              : `Events in ${month.toLocaleString("en-GB", { month: "long" })}`}
          </h3>
          {monthList.length === 0 ? (
            <Card><p className="font-sans text-sm text-ink/60">No events {selectedDay ? "on this day" : "this month"} for the selected layers.</p></Card>
          ) : (
            <div className="grid gap-5">
              {monthList.map((e) => <EventCard key={e.id} e={e} {...cardProps} />)}
            </div>
          )}
        </>
      ) : shown.length === 0 ? (
        <Card><p className="font-sans text-sm text-ink/60">No events match the selected layers.</p></Card>
      ) : (
        <div className="grid gap-5">
          {shown.map((e) => <EventCard key={e.id} e={e} {...cardProps} />)}
        </div>
      )}

      {modal !== null && (
        <EventModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {rsvpModal && <RsvpModal event={rsvpModal} onClose={() => setRsvpModal(null)} />}
    </div>
  );
}
